import { Component, type ComponentType, type ErrorInfo, type PropsWithChildren, type ReactNode } from 'react';
import { Button } from '@direct-frontend/components';
import { logError } from '../../lib/rum';
import { ErrorBlankSad } from '../ErrorBlank';
import { CustomError } from './CustomError';

export type ErrorBoundaryRenderWrapper = (props: PropsWithChildren) => ReactNode;

type ErrorBoundaryWrapperProps = PropsWithChildren<{
    /** При изменении идентификатора в родителе - сбрасывается состояние ошибки в компоненте */
    uniqueId?: string;
    renderWrapper?: ErrorBoundaryRenderWrapper;
    errorFallback?: ComponentType<{ error: unknown }>;
}>;

type State = {
    uniqueId?: string;
    hasError: boolean;
    error: null | Error;
};

const defaultRenderWrapper: ErrorBoundaryRenderWrapper = ({ children }) => children;

/*
 * Обертка для отлова ошибок в коде у потомков в реакт дереве
 * @kind component
 */
export class ErrorBoundaryWrapper extends Component<ErrorBoundaryWrapperProps, State> {
    override state: State = { hasError: false, error: null };

    static getDerivedStateFromProps(props: ErrorBoundaryWrapperProps, state: State) {
        if (props.uniqueId !== state.uniqueId) {
            return {
                uniqueId: props.uniqueId,
                hasError: false,
                error: null,
            };
        }

        return null;
    }

    /** Вызывается React при ошибке в потомках (Error Boundary API). @public */
    static getDerivedStateFromError(error: Error) {
        return { hasError: true, error };
    }

    override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error(error);
        console.error(errorInfo);

        logError(
            {
                level: 'error',
                additional: {
                    react_error_handler: 'error_boundary',
                    react_component_stack: errorInfo.componentStack,
                },
            },
            error
        );
    }

    private handleReload = () => {
        if (window?.location?.reload) {
            window?.location?.reload();
        } else {
            this.setState({ hasError: false });
        }
    };

    override render() {
        const { errorFallback: ErrorComponent, renderWrapper = defaultRenderWrapper } = this.props;

        let children: ReactNode;

        if (this.state.hasError) {
            if (ErrorComponent) {
                children = <ErrorComponent error={this.state.error} />;
            } else if (this.state.error instanceof CustomError) {
                children = this.state.error.element;
            } else {
                children = (
                    <ErrorBlankSad
                        title="Что-то пошло не так"
                        subtitle={
                            this.state.error && __IS_DEV__ ? (
                                <details style={{ maxWidth: 300 }}>
                                    <summary>Trace</summary>
                                    <code>
                                        <pre style={{ overflowX: 'auto' }}>{this.state.error.stack}</pre>
                                    </code>
                                </details>
                            ) : undefined
                        }
                        controls={
                            <Button type="button" color="accent" size="m" onClick={this.handleReload}>
                                Перезагрузить
                            </Button>
                        }
                    />
                );
            }

            return renderWrapper({ children });
        }

        return this.props.children;
    }
}
